import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { concatMap, map, Observable, of } from 'rxjs';
import { forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppGlobalService } from './app.global.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class UploadAttachmentService {
  private http = inject(HttpClient);
  private appGlobalService = inject(AppGlobalService);

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'x-rappit-application': this.appGlobalService.get('appId'),
      'x-rappit-organization': this.appGlobalService.get('orgId')
    });
  }

  private prepareFormData(file: any, description: string, objectId: any): FormData {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('modelName', file ? (file as any)['modelName'] : undefined);
    formData.append('fieldName', file ? (file as any)['fieldName'] : undefined);
    formData.append('modelKey', objectId ?? '');
    formData.append('appId', this.appGlobalService.get('appId') ?? '');
    formData.append('orgId', this.appGlobalService.get('orgId') ?? '');
    formData.append('fileDesc', description || '');
    return formData;
  }

  private uploadSingleFile(file: File, url: string, description: string, objectId: any): Observable<any> {
    let uploadUrl = '';
    if (!url) {
      uploadUrl = `/${environment.apihost}/attachments/upload`;
    } else if (!url.startsWith('/')) {
      uploadUrl = `/${url}`;
    } else {
      uploadUrl = url;
    }
    const formData = this.prepareFormData(file, description, objectId);
    return this.http.post(uploadUrl, formData, { headers: this.getHeaders() });
  }

  /**
   * Uploads one or multiple files and optionally finalizes the upload.
   * @param files File or array of Files to upload.
   * @param url Optional upload URL.
   * @param description Optional file description.
   * @param objectId Object ID related to the upload.
   * @param form Optional form data.
   */
  uploadFile(
    files: File | File[],
    url: string,
    description: string,
    objectId: any,
    form?: any,
    fileBasicData?: any
  ): Observable<any> {
    if (files instanceof File) {
      return this.uploadSingleFile(files, url, description, objectId);
    } else if (Array.isArray(files) && files.length > 0) {
      // Upload all files simultaneously
      const uploads = files.map(file =>
        this.uploadSingleFile(file, url, description, objectId)
      );
      return forkJoin(uploads).pipe(
        concatMap((results: any[]) => {
          const attachments = results?.flat().map((item, idx) => {
            const base = {
              fileName: item.fileName,
              id: item.id
            };
            if (fileBasicData) {
                return {
                  ...fileBasicData,
                  title: item.fileName,
                  url: `/${environment.apihost}/attachments/download/attachment/${item.sid}/${this.appGlobalService.get('appId')}`,
                  key: item.sid,
                  sid: item.sid
                };
              }
              return base;
            }) || [];

          // Call final API after all uploads, send payload as FormData
          const finalUrl = url || `/${environment.apihost}/attachments/upload`;
          const formData = this.prepareFormData(null, description, objectId);
          formData.append('attachments', JSON.stringify(attachments));
          formData.append('saveRecord', 'true');

          return this.http.post(
            finalUrl,
            formData,
            { headers: this.getHeaders() }
          );
        })
      );
    } else {
      return of([]);
    }
  }

  uploadMultipleFiles(files: any, url: string, description: string, objectId: any): Observable<any> {
    let uploadUrl = '';
    if (!url) {
      uploadUrl = `/${environment.apihost}/attachments/upload`;
    } else if (!url.startsWith('/')) {
      uploadUrl = `/${url}`;
    }

    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('description', description || '');
    formData.append('modelName', files.modelName);
    formData.append('modelKey', objectId);
    formData.append('fieldName', files.fieldName);
    formData.append('fileDesc', description || '');
    formData.append('appId', this.appGlobalService.get('appId'));
    formData.append('orgId', this.appGlobalService.get('orgId'));

    return this.http.post(uploadUrl, formData, { headers: this.getHeaders() });
  }
  getData(url: string): Observable<any> {
    return this.http.get(url);
  }

  saveAddedFiles(splittedData, id, form) {
    return Observable.create((observer) => {
      const dataToResend = {};
      if (splittedData.files) {
        const totalFiles = Object.keys(splittedData.files).length;
        let index = 1;
        for (const file in splittedData.files) {
          if (Object.prototype.hasOwnProperty.call(splittedData.files, file)) {
            this.uploadFile(splittedData.files[file], '', '', id, form).subscribe({
              next: (res) => {
                if (res !== 'Nil') {
                  const curFiles = splittedData.files[file];
                  let fieldName = '';
                  let fieldValue;

                  fieldName = file;
                  if (curFiles instanceof Array) {
                    fieldValue = res; // res.join(',').split(',');
                  } else {
                    fieldValue = res[0];
                  }
                  dataToResend[fieldName] = fieldValue;
                  if (index === totalFiles) {
                    const finalData = { data: { ...splittedData.data, ...dataToResend } };
                    observer.next(dataToResend);
                    observer.complete();
                  }
                }
                index = index + 1;
              },
              error: (err) => {
                console.log('Error===', err);
              }
            });
          }
        }
      }
    });
  }

  downloadFile(url: string): Observable<Blob> {
    return this.http.get(`/${environment.apihost}/${url}`, {
      responseType: 'blob',
      headers: {
        'x-rappit-application': this.appGlobalService.get('appId'),
        'x-rappit-organization': this.appGlobalService.get('orgId')
      }
    });
  }
}
